import request from "@/lib/request/request";

// 导出任务状态枚举
export enum ExportTaskStatus {
  WAITING = 0, // 等待中
  RUNNING = 1, // 执行中
  SUCCESS = 2, // 执行成功
  FAILED = 3, // 执行失败
  PAUSED = 5, // 已暂停
  DELETED = 10, // 已删除
  EXPIRED = 11 // 已过期
}

// 导出任务类型枚举
export enum ExportTaskType {
  CAND = "cand", // HR端候选人列表导出
  CAND_ANS = "candAns", // HR端候选人答案导出
  EXAM = "exam", // HR端面试配置导出
  EXAM_QUES = "examQues" // HR端面试题目导出
}

// 导出任务版本枚举
export enum ExportVersion {
  V0 = 0, // 旧版本数据
  V1 = 1 // 新版本数据
}

// 查询参数接口 - 匹配Go接口文档
export interface ExportTaskListParams {
  page: number;
  pageSize: number;
  features?: {
    exportId?: number; // 导出任务id
    name?: string; // 任务名
    taskStatusList?: ExportTaskStatus[]; // 任务状态列表
    taskTypeList?: ExportTaskType[]; // 任务类型列表
    versionList?: ExportVersion[]; // 导出任务版本列表
    userId?: number; // 用户id (单个，不是数组)
    createdTime?: [string, string]; // 创建时间 [开始时间, 结束时间] - 字符串格式
  };
}

// 导出任务项接口 - 匹配新API结构
export interface ExportTaskItem {
  version: number; // 导出任务版本
  exportId: number; // 任务id
  companyId: number; // 公司id
  userId: number; // 用户id
  userCompany?: {
    // 公司用户信息
    nickname?: string;
    userId?: number;
  };
  name: string; // 任务名
  createdTime: string; // 创建时间
  taskStatus: number; // 任务状态
  message: string; // 执行信息
  count: number; // 数据数量
  fileCount: number; // 文件数量
  taskType: string; // 任务类型
  startTime: string; // 开始时间
  endTime: string; // 结束时间
  sort: number; // 排序优先级
  queueType: number; // 排序队列类型

  // 兼容旧版本字段（可能不存在）
  status?: number; // 兼容旧的状态字段
  exportType?: string; // 兼容旧的类型字段
  beginTime?: string; // 兼容旧的开始时间字段
  url?: string; // 下载链接
  passwd?: string; // 下载密码
  zipUrl?: string; // 压缩文件下载链接
  urlList?: string[]; // 文件列表下载链接
  userInfo?: {
    // 兼容旧的用户信息字段
    nickname?: string;
    userId?: number;
  };
}

// 列表响应接口
export interface ExportTaskListResponse {
  list: ExportTaskItem[];
  count: number;
}

// 删除参数接口
export interface DeleteExportTaskParams {
  exportId: number;
}

// 任务控制参数接口
export interface TaskControlParams {
  taskIdList: number[];
}

// 获取结果参数接口
export interface GetExportResultParams {
  exportId: number;
}

// 用户搜索参数接口
export interface SearchUserParams {
  features: {
    nickname: string;
  };
}

// 用户信息接口
export interface UserInfo {
  userId: number;
  nickname: string;
  mobile: string;
  email: string;
  avatarUrl: string;
  roleV2: number;
  businessTypeList: string[];
  noCompanyLink: boolean;
}

// 用户列表响应接口
export interface UserListResponse {
  list: UserInfo[];
}

// 候选人特征接口请求参数
export interface CandidateListFeaturesParams {
  companyId: string;
  examType?: string;
}

// 候选人特征接口响应数据
export interface CandidateListFeaturesResponse {
  tag: string[];
  positions: string[];
  paramName: string[];
  user: UserInfo[];
}

/**
 * 获取导出任务列表 - 使用新的v2 API
 */
export function getExportTaskList(
  params: ExportTaskListParams
): Promise<{ data: ExportTaskListResponse; code: number; message: string }> {
  return request.post("/v2/aiExam/export/list", params).then(resp => {
    return resp.data;
  });
}

/**
 * 删除导出任务 - 使用新的v2 API
 */
export function deleteExportTask(
  params: DeleteExportTaskParams
): Promise<{ code: number; message: string }> {
  return request.post("/v2/aiExam/export/delete", params).then(resp => {
    return resp.data;
  });
}

/**
 * 暂停导出任务 - 等待中 -> 已暂停
 */
export function pauseExportTask(params: {
  exportId: number;
}): Promise<{ code: number; message: string }> {
  return request.post("/v2/aiExam/export/pause", params).then(resp => {
    return resp.data;
  });
}

/**
 * 重新排队执行导出任务 - 执行失败/暂停 -> 排队中
 */
export function restartExportTask(params: {
  exportId: number;
}): Promise<{ code: number; message: string }> {
  return request.post("/v2/aiExam/export/restart", params).then(resp => {
    return resp.data;
  });
}

/**
 * 获取导出结果/下载链接 - 使用新的v2 API
 */
export function getExportResult(
  params: GetExportResultParams
): Promise<{ data: any; code: number; message: string }> {
  return request.post("/v2/aiExam/export/getResult", params).then(resp => {
    return resp.data;
  });
}

/**
 * 获取用户列表 - 用于创建人筛选
 */
export function getUserList(
  params: SearchUserParams
): Promise<{ data: UserListResponse; code: number; message: string }> {
  return request.post("/v2/aiExam/export/listUser", params).then(resp => {
    return resp.data;
  });
}

/**
 * 记录下载操作 - 用于统计
 */
export function trackDownload(params: {
  filename?: string;
  taskId: number;
  url: string;
}): Promise<{ code: number; message: string }> {
  return request
    .post("/v2/aiExam/common/track/operator/download", params)
    .then(resp => {
      return resp.data;
    });
}

/**
 * 获取候选人特征信息（包含用户列表） - 复用候选人页面的接口
 */
export function getCandidateListFeatures(
  params: CandidateListFeaturesParams
): Promise<{
  data: CandidateListFeaturesResponse;
  code: number;
  message: string;
}> {
  return request.post("/aiExam/candidate/listFeatures", params).then(resp => {
    return resp.data;
  });
}
